import React, { useCallback, useEffect, useRef } from 'react';
import {
  I18nManager,
  Platform,
  ScrollView,
  type LayoutRectangle,
  type StyleProp,
  type View,
  type ViewStyle,
} from 'react-native';

import { Box, useTheme } from 'app/shopify/theme';
import { TabItem, type TabItemState } from './TabItem';

export interface TabOption {
  value: string;
  label: string;
  badge?: string | number;
  disabled?: boolean;
  accessibilityLabel?: string;
  /** Pair nativeID with a panel's aria-labelledby on web. */
  nativeID?: string;
  /** ID of the associated web tabpanel, owned and rendered by the caller. */
  panelID?: string;
}

interface TabsBaseProps {
  options: readonly TabOption[];
  value: string;
  onValueChange: (value: string) => void;
  accessibilityLabel: string;
  disabled?: boolean;
  state?: TabItemState;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}

export type TabsProps = TabsBaseProps &
  ({ orientation?: 'horizontal'; scroll?: boolean } | { orientation: 'vertical'; scroll?: false });

/** Controlled tabs. Horizontal tabs scroll by default; scroll=false distributes their width equally. */
export function Tabs({
  options,
  value,
  onValueChange,
  accessibilityLabel,
  orientation = 'horizontal',
  scroll = true,
  disabled = false,
  state,
  style,
  testID,
}: TabsProps) {
  const theme = useTheme();
  const vertical = orientation === 'vertical';
  const scrollable = !vertical && scroll;
  const isDisabled = disabled || state === 'disabled';
  const tabRefs = useRef(new Map<string, View>());
  const layouts = useRef(new Map<string, LayoutRectangle>());
  const scrollRef = useRef<ScrollView>(null);
  const viewportWidth = useRef(0);
  const scrollOffset = useRef(0);
  const enabledOptions = options.filter((option) => !isDisabled && !option.disabled);
  const tabStop = enabledOptions.find((option) => option.value === value)?.value ?? enabledOptions[0]?.value;

  const pageMargin = theme.spacing['spacing.semantic.page-margin'];
  // Stable identity is needed by the selection effect and layout/scroll event handlers.
  const revealTab = useCallback(
    (tabValue: string) => {
      const layout = layouts.current.get(tabValue);
      if (!scrollable || !layout || viewportWidth.current === 0) return;
      const start = layout.x - pageMargin;
      const end = layout.x + layout.width + pageMargin;
      const offset = scrollOffset.current;
      if (start < offset || end > offset + viewportWidth.current) {
        const x = Math.max(0, start < offset ? start : end - viewportWidth.current);
        scrollOffset.current = x;
        scrollRef.current?.scrollTo({ x, animated: false });
      }
    },
    [pageMargin, scrollable],
  );

  useEffect(() => {
    revealTab(value);
  }, [value, revealTab]);

  function handleKeyDown(event: React.KeyboardEvent<HTMLElement>, currentValue: string) {
    if (event.altKey || event.ctrlKey || event.metaKey || isDisabled) return;
    const nextKey = vertical ? 'ArrowDown' : I18nManager.isRTL ? 'ArrowLeft' : 'ArrowRight';
    const previousKey = vertical ? 'ArrowUp' : I18nManager.isRTL ? 'ArrowRight' : 'ArrowLeft';
    const currentIndex = enabledOptions.findIndex((option) => option.value === currentValue);
    let nextIndex: number;
    if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = enabledOptions.length - 1;
    else if (event.key === nextKey) nextIndex = (currentIndex + 1) % enabledOptions.length;
    else if (event.key === previousKey) nextIndex = (currentIndex - 1 + enabledOptions.length) % enabledOptions.length;
    else return;
    const next = enabledOptions[nextIndex];
    if (!next) return;
    event.preventDefault();
    tabRefs.current.get(next.value)?.focus();
    revealTab(next.value);
    if (next.value !== value) onValueChange(next.value);
  }

  const items = options.map((option) => (
    <TabItem
      key={option.value}
      ref={(node) => {
        if (node) tabRefs.current.set(option.value, node);
        else tabRefs.current.delete(option.value);
      }}
      nativeID={option.nativeID}
      label={option.label}
      badge={option.badge}
      accessibilityLabel={option.accessibilityLabel}
      selected={option.value === value}
      disabled={isDisabled || option.disabled}
      state={state}
      width={!vertical && !scrollable ? 'fill' : 'hug'}
      style={scrollable ? { flexShrink: 0 } : undefined}
      tabIndex={option.value === tabStop ? 0 : -1}
      onPress={() => {
        if (!isDisabled && !option.disabled && option.value !== value) onValueChange(option.value);
      }}
      onLayout={(event) => {
        layouts.current.set(option.value, event.nativeEvent.layout);
        if (option.value === value) revealTab(value);
      }}
      {...(Platform.OS === 'web'
        ? {
            'aria-controls': option.panelID,
            onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => handleKeyDown(event, option.value),
          }
        : {})}
    />
  ));

  const accessibilityProps = {
    accessibilityRole: 'tablist' as const,
    accessibilityLabel,
    ...(Platform.OS === 'web' ? { 'aria-orientation': orientation } : {}),
  };

  return (
    <Box style={style} testID={testID} alignSelf={vertical ? 'flex-start' : 'stretch'}>
      {!vertical ? (
        <Box
          pointerEvents="none"
          position="absolute"
          bottom={0}
          left={0}
          right={0}
          height={theme.borderWidths['border-width.subtle']}
          backgroundColor="border.divider"
        />
      ) : null}
      {scrollable ? (
        <ScrollView
          {...accessibilityProps}
          ref={scrollRef}
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flexGrow: 0 }}
          contentContainerStyle={{
            flexGrow: 1,
            gap: theme.spacing['spacing.general.x5'],
            paddingHorizontal: theme.spacing['spacing.semantic.page-margin'],
          }}
          onLayout={(event) => {
            viewportWidth.current = event.nativeEvent.layout.width;
            revealTab(value);
          }}
          onScroll={(event) => {
            scrollOffset.current = event.nativeEvent.contentOffset.x;
          }}
          scrollEventThrottle={16}
        >
          {items}
        </ScrollView>
      ) : (
        <Box
          {...accessibilityProps}
          flexDirection={vertical ? 'column' : 'row'}
          alignItems={vertical ? 'flex-start' : 'stretch'}
        >
          {items}
        </Box>
      )}
    </Box>
  );
}
